'use client';

import { useMemo, useState } from 'react';
import { Button, Modal, toast } from '@mokoboko/components';

import { GuestDetailsList, TermsLoadError } from '@/components';
import { addGuestsLabel, toGuestPayload } from '@/helpers/guest-pass';
import { getTermsTagValues } from '@/helpers/terms';
import { useGuestDetailsForm } from '@/hooks/use-guest-details-form';
import { AnalyticsEvent, track } from '@/lib/analytics/tracker';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import { getErrorCode, getErrorMessage } from '@/lib/error-utils';
import { isLoginRequired } from '@/lib/http-adapter';
import type { ReceiptGuest } from '@/lib/types';
import { phoneCountryFor } from '@/lib/phone-country';
import { addOrderGuests } from '@/services/order';
import useResolvedTerms from '@/state/terms';
import useTenant from '@/state/tenant';

// The class time changed under the page: the host's spot went, it started, or
// it moved inside the notice window. The page re-reads the order and the row
// goes with it.
const CLOSED_CODES = [
  'GUEST_HOST_NOT_BOOKED',
  'CLASS_ALREADY_STARTED',
  'BOOKING_RULE_VIOLATION',
];

export const AddGuestModal = ({
  orderId,
  occurrence,
  classId,
  className,
  whenLabel,
  minGuests,
  maxGuests,
  newGuestsOnly,
  source,
  hostEmail,
  hostPhone,
  onClose,
  onAdded,
  onRecheck,
  onLoginRequired,
}: {
  orderId: string;
  occurrence: {
    slot_id: string | null;
    override_slot_id: string | null;
    start_time: string;
  };
  classId: string;
  className: string;
  whenLabel: string;
  // Fewer would leave the host's group below the class's min spots.
  minGuests: number;
  maxGuests: number;
  newGuestsOnly: boolean;
  source: 'class_pack' | 'subscription';
  hostEmail?: string | null;
  hostPhone?: { country_code?: string | null; phone?: string | null } | null;
  onClose: () => void;
  onAdded: (guests: ReceiptGuest[]) => void;
  onRecheck: () => void;
  onLoginRequired: () => void;
}) => {
  const { data: tenant } = useTenant();
  const studioName = tenant?.name || 'the studio';
  const {
    docs: termsDocs,
    isLoading: isTermsLoading,
    isError: isTermsError,
    refetch: refetchTerms,
  } = useResolvedTerms({ type: 'CLASS', entityIds: [classId] });
  const tagValues = useMemo(() => getTermsTagValues(tenant), [tenant]);
  const guests = useGuestDetailsForm({
    hostEmail,
    termsDocs,
    defaultCountry: phoneCountryFor(hostPhone, tenant),
    initialCount: minGuests,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const count = guests.fields.length;

  const handleSubmit = async () => {
    if (!(await guests.validate())) return;
    const payload = toGuestPayload(guests.form.getValues('guests'), termsDocs);
    setIsSubmitting(true);
    try {
      const result = await addOrderGuests(orderId, {
        ...(occurrence.slot_id
          ? { slot_id: occurrence.slot_id }
          : { override_slot_id: occurrence.override_slot_id }),
        start_time: occurrence.start_time,
        guests: payload,
      });
      track({
        event: AnalyticsEvent.GUEST_ADDED_LATER,
        properties: {
          class_id: classId,
          class_name: className,
          guest_count: payload.length,
          source,
          new_guests_only: newGuestsOnly,
        },
      });
      onAdded(result?.guests ?? []);
    } catch (error) {
      if (isLoginRequired(error)) {
        onLoginRequired();
        return;
      }
      const code = getErrorCode(error);
      const refusal = guests.applyServerError(error);
      if (refusal) {
        track({
          event: AnalyticsEvent.GUEST_ADD_REFUSED,
          properties: { error_code: code ?? '', guest_count: payload.length },
        });
        if (refusal.kind === 'toast') {
          toast.error(refusal.message);
          onClose();
        }
        // Passes or spots ran out: the re-read lowers how many cards fit.
        if (refusal.kind !== 'field') onRecheck();
        return;
      }
      if (code && CLOSED_CODES.includes(code)) {
        toast.error(
          getErrorMessage(error, "Guests can't be added to this class now.")
        );
        onClose();
        onRecheck();
        return;
      }
      toast.error(getErrorMessage(error, GENERIC_ERROR_MESSAGE));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal.Root
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <Modal.Content size="md" hasCloseButton isPreventClose={isSubmitting}>
        <Modal.Header>
          <Modal.Title>Add a guest</Modal.Title>
          <Modal.Description>
            {className} · {whenLabel}
            {newGuestsOnly && (
              <>
                <br />
                Guest passes are for people new to {studioName}.
              </>
            )}
          </Modal.Description>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-5">
            {isTermsError && <TermsLoadError onRetry={() => refetchTerms()} />}

            <GuestDetailsList
              guests={guests}
              maxGuests={maxGuests}
              terms={{ docs: termsDocs, tagValues, studioName }}
              canRemove={count > minGuests}
              onRemoveGuest={guests.removeGuest}
              variant="stacked"
              disabled={isSubmitting}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button
              variant="secondary-gray"
              onClick={onClose}
              disabled={isSubmitting}
              className="ml-auto"
            >
              Cancel
            </Button>
          </Modal.Close>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isProcessing={isSubmitting}
            disabled={
              isTermsLoading ||
              isTermsError ||
              count < minGuests ||
              count > maxGuests
            }
          >
            {addGuestsLabel(count)}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
